import {
  RELEASE_MAIL_MODES,
  type ReleaseMailAdmin,
  type ReleaseMailMode,
  type ReleaseMailSettings,
  type ReleaseMailTestResult,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { dateTime, plural } from '../../lib/format';
import { Button } from '../../ui/button';
import { ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Input } from '../../ui/form';
import { Panel } from '../../ui/layout';

const KEY = ['admin', 'release-mail'];
const MODES = Object.keys(RELEASE_MAIL_MODES) as ReleaseMailMode[];

const sameSettings = (a: ReleaseMailSettings, b: ReleaseMailSettings) =>
  a.mode === b.mode && a.admins === b.admins && [...a.userIds].sort().join() === [...b.userIds].sort().join();

/**
 * Письма об обновлении портала (АД-09, БЗ-04): кому после выкладки с новым по его разделам приходит письмо
 * «Портал обновлён» — администраторам компании (по умолчанию) и выбранным сотрудникам или всем сотрудникам
 * компании, — и пробное письмо себе.
 */
export function ReleaseMailPanel() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<ReleaseMailAdmin>('/admin/release-mail') });
  const [form, setForm] = useState<ReleaseMailSettings | null>(null);
  const [search, setSearch] = useState('');
  const [test, setTest] = useState<ReleaseMailTestResult | null>(null);
  useEffect(() => {
    if (q.data && !form) setForm(q.data.settings);
  }, [q.data, form]);
  // Ошибка прошлого действия не остаётся на экране после следующего
  const save = useMutation({
    mutationFn: (body: ReleaseMailSettings) =>
      api<ReleaseMailAdmin>('/admin/release-mail', { method: 'PUT', body }),
    onMutate: () => send.reset(),
    onSuccess: (x) => {
      qc.setQueryData(KEY, x);
      setForm(x.settings);
      toast('Получатели писем об обновлении сохранены');
    },
  });
  const send = useMutation({
    mutationFn: () => api<ReleaseMailTestResult>('/admin/release-mail/test', { method: 'POST' }),
    onMutate: () => {
      setTest(null);
      save.reset();
    },
    onSuccess: (x) => {
      setTest(x);
      // Пробное письмо попадает в журнал доставки «Уведомления сотрудникам»
      void qc.invalidateQueries({ queryKey: ['admin', 'notifications'] });
    },
  });

  const people = q.data?.people ?? [];
  const chosen = useMemo(() => new Set(form?.userIds ?? []), [form]);
  const needle = search.trim().toLocaleLowerCase('ru');
  const shown = needle
    ? people.filter((p) => `${p.fullName} ${p.email}`.toLocaleLowerCase('ru').includes(needle))
    : people;
  const chosenNames = people.filter((p) => chosen.has(p.userId)).map((p) => p.fullName);
  const adminNames = people.filter((p) => p.admin).map((p) => p.fullName);
  // Администратор при включённой отметке получает письмо и без выбора в списке
  const byDefault = (p: { admin: boolean }) => Boolean(form?.admins && p.admin);
  const willGet = form
    ? people.filter(
        (p) => !p.emailOff && p.email && (form.mode === 'all' || chosen.has(p.userId) || byDefault(p)),
      ).length
    : 0;
  const recipientsText = (): string => {
    if (q.data?.demo) return 'никто — в демо-компании письма не уходят';
    if (!willGet) return 'никто — выберите сотрудников';
    const n = plural(willGet, 'человек', 'человека', 'человек');
    return q.data?.email.configured ? n : `${n}, когда на сервере будет настроена почта`;
  };

  const toggle = (userId: string, on: boolean) => {
    if (!form) return;
    const next = new Set(form.userIds);
    if (on) next.add(userId);
    else next.delete(userId);
    setForm({ ...form, userIds: [...next] });
  };

  return (
    <Panel title="Письма об обновлении портала" meta="АД-09, БЗ-04; изменения — в «Истории изменений»">
      <div className="flex max-w-2xl flex-col gap-4 text-[14px]">
        <p className="text-ink-2">
          После каждого обновления портала, в котором есть новое по их разделам, администраторы компании и
          выбранные сотрудники получают письмо: когда обновился портал и что нового. Письмо уходит через
          несколько минут после выкладки, когда портал снова работает. Суммы в письма не попадают.
        </p>
        {q.isPending || !form ? (
          q.isError ? (
            <ErrorBox error={q.error} />
          ) : (
            <Spinner />
          )
        ) : q.isError ? (
          <ErrorBox error={q.error} />
        ) : (
          <>
            <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[200px_1fr]">
              <dt className="text-ink-2">Почта</dt>
              <dd>
                {q.data.email.configured
                  ? `настроена, отправитель ${q.data.email.from}`
                  : 'не настроена — нужны SMTP_URL и MAIL_FROM в настройках сервера'}
              </dd>
              <dt className="text-ink-2">Последнее обновление</dt>
              <dd>
                {q.data.last ? (
                  <>
                    {dateTime(q.data.last.at)}, версия <span className="num">{q.data.last.commit}</span>
                    {' — '}
                    {q.data.last.newCount
                      ? `${plural(q.data.last.newCount, 'новая запись', 'новые записи', 'новых записей')} «Что нового»`
                      : 'без новых записей «Что нового»'}
                  </>
                ) : (
                  'после включения писем обновлений ещё не было'
                )}
              </dd>
              <dt className="text-ink-2">Получат письмо</dt>
              <dd>{recipientsText()}</dd>
            </dl>
            {q.data.commit === 'local' ? (
              <p className="rounded-md bg-warning-soft px-3 py-2.5">
                Номер версии сервера не задан (RAILWAY_GIT_COMMIT_SHA), поэтому портал не узнаёт о своих
                обновлениях и писем после них не пришлёт. Пробное письмо работает.
              </p>
            ) : null}
            {q.data.demo ? (
              <p className="rounded-md bg-warning-soft px-3 py-2.5">
                В демо-компании письма не отправляются: настройка показана для примера.
              </p>
            ) : null}
            <fieldset className="flex flex-col">
              <legend className="mb-1 text-[13px] font-medium text-ink">Кому отправлять</legend>
              {MODES.map((m) => (
                <label key={m} className="flex min-h-11 cursor-pointer items-center gap-2.5 md:min-h-9">
                  <input
                    type="radio"
                    name="release-mail-mode"
                    value={m}
                    checked={form.mode === m}
                    onChange={() => setForm({ ...form, mode: m })}
                    className="size-4 shrink-0 cursor-pointer accent-[var(--color-ink)]"
                  />
                  <span className="text-ink">{RELEASE_MAIL_MODES[m]}</span>
                </label>
              ))}
            </fieldset>
            {form.mode === 'selected' ? (
              <Checkbox
                label="Администраторам компании — всегда"
                description={`Владелец и те, кто ведёт настройки компании, получают письмо без отметки в списке${
                  adminNames.length ? `: ${adminNames.join(', ')}` : ''
                }.`}
                className="[overflow-wrap:anywhere]"
                checked={form.admins}
                onChange={(e) => setForm({ ...form, admins: e.target.checked })}
              />
            ) : null}
            {form.mode === 'selected' ? (
              <section className="flex flex-col gap-2">
                <h3 className="text-[13px] font-medium text-ink">
                  Сотрудники · выбрано {form.userIds.length} из {people.length}
                  {form.admins && adminNames.length
                    ? ` · администраторов ${adminNames.length}, получают всегда`
                    : ''}
                </h3>
                {chosenNames.length ? (
                  <p className="text-ink-2 [overflow-wrap:anywhere]">Выбраны: {chosenNames.join(', ')}</p>
                ) : null}
                {people.length > 8 ? (
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Найти по имени или почте"
                    aria-label="Найти сотрудника"
                    className="h-11 md:h-10"
                  />
                ) : null}
                <ul className="flex max-h-80 flex-col divide-y divide-line overflow-y-auto rounded-md border border-line">
                  {shown.map((p) => (
                    <li key={p.userId}>
                      {/* Нажимается вся строка, а не только квадрат: на телефоне — не меньше 44 px */}
                      <Checkbox
                        className="px-3 py-2.5 [overflow-wrap:anywhere]"
                        label={p.fullName}
                        description={[
                          p.email,
                          byDefault(p) ? 'администратор — получает всегда' : null,
                          p.emailOff ? 'почта выключена в профиле, письмо не уйдёт' : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                        checked={chosen.has(p.userId) || byDefault(p)}
                        disabled={byDefault(p)}
                        onChange={(e) => toggle(p.userId, e.target.checked)}
                      />
                    </li>
                  ))}
                  {!shown.length ? (
                    <li className="px-3 py-3 text-ink-2">
                      {people.length ? 'Никого не нашли' : 'Сотрудников пока нет'}
                    </li>
                  ) : null}
                </ul>
              </section>
            ) : (
              <p className="text-ink-2">
                Письмо получат все действующие сотрудники компании, у кого почта включена в профиле. Внешние
                участники — заказчик, поставщики, подрядчики — писем не получают.
              </p>
            )}
            <ErrorBox error={save.error ?? send.error} />
            {test ? (
              <p
                role="status"
                className={
                  test.status === 'sent'
                    ? 'rounded-md bg-success-soft px-3 py-2.5'
                    : 'rounded-md bg-warning-soft px-3 py-2.5 [overflow-wrap:anywhere]'
                }
              >
                {test.status === 'sent'
                  ? `Пробное письмо отправлено на ${test.to}.`
                  : test.status === 'pending'
                    ? `Письмо на ${test.to} не ушло с первой попытки, портал повторит отправку${test.error ? `: ${test.error}` : ''}.`
                    : `Письмо на ${test.to} не ушло${test.error ? `: ${test.error}` : ''}.`}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                loading={save.isPending}
                disabled={sameSettings(form, q.data.settings)}
                onClick={() => save.mutate(form)}
              >
                Сохранить
              </Button>
              <Button
                loading={send.isPending}
                disabled={q.data.demo || !q.data.email.configured}
                onClick={() => send.mutate()}
              >
                Прислать пробное письмо мне
              </Button>
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}
